/**
 * CounterModelGrid — responsive grid of counter-models used by Dual Pair
 * Guarantee and Immunity Closure Guarantee explainers to ground the
 * "strictly stronger" claim concretely.
 *
 * Layout: an auto-fit grid of cards with a minimum column width of
 * ~16rem. Two-up on wide viewports, single-column when the viewport is
 * narrow (works without a media-query thanks to `auto-fit minmax`).
 *
 * Each cell is rendered with a dashed border to signal "this is what
 * would happen *without* the joint guarantee" rather than a positive
 * structural statement.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY } from "../../design-system/tokens.js";

export interface CounterModel {
  /** Stable id for React reconciliation; falls back to the array index. */
  readonly id?: string;
  readonly title: string;
  readonly body: React.ReactNode;
}

export interface CounterModelGridProps {
  readonly counterModels: readonly CounterModel[];
  /** Optional aria-label override; defaults to "Counter-models". */
  readonly ariaLabel?: string;
  /**
   * Minimum column width before the grid wraps to a new row. Cells flow
   * into as many columns as fit at this minimum; one column on narrow
   * viewports. Defaults to `"16rem"`.
   */
  readonly minColumnWidth?: string;
}

export const CounterModelGrid = React.memo(function CounterModelGrid({
  counterModels,
  ariaLabel = "Counter-models",
  minColumnWidth = "16rem",
}: CounterModelGridProps): React.JSX.Element {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${minColumnWidth}, 1fr))`,
        gap: "0.5rem",
        marginTop: "0.75rem",
      }}
      aria-label={ariaLabel}
    >
      {counterModels.map((cm, index) => (
        <div
          key={cm.id ?? `${index}:${cm.title}`}
          style={{
            padding: "0.625rem",
            border: `1px dashed ${GRAY[400]}`,
            borderRadius: "0.375rem",
            backgroundColor: "white",
          }}
        >
          <div style={{ fontWeight: 700, color: GRAY[900], marginBottom: "0.25rem" }}>
            {cm.title}
          </div>
          <div style={{ color: GRAY[700], fontSize: "0.8125rem" }}>{cm.body}</div>
        </div>
      ))}
    </div>
  );
});
